import React, { useState } from 'react';
import { Search, Star, TrendingUp, Package, BarChart3, FileText, Clock, MessageSquare, ShoppingCart, Zap, Users, Calendar, DollarSign, ChevronRight, Download, Check, X } from 'lucide-react';

interface App {
  id: string;
  name: string;
  tagline: string;
  description: string;
  category: string;
  price: number | 'free';
  rating: number;
  reviews: number;
  icon: JSX.Element;
  color: string;
  features: string[];
  screenshots?: string[];
  popular?: boolean;
  new?: boolean;
}

const placeholderApps: App[] = [
  {
    id: 'inventory-pro',
    name: 'Inventory Pro',
    tagline: 'Complete stock management solution',
    description: 'Manage your inventory, track stock levels, and handle product sales with ease. Perfect for businesses that need to keep track of physical products.',
    category: 'Operations',
    price: 29.99,
    rating: 4.8,
    reviews: 234,
    icon: <Package className="h-8 w-8" />,
    color: 'bg-blue-500',
    features: [
      'Real-time stock tracking',
      'Low stock alerts',
      'Product photos',
      'Barcode scanning',
      'Purchase orders',
      'Stock transactions history'
    ],
    popular: true
  },
  {
    id: 'automations-plus',
    name: 'Automations Plus',
    tagline: 'Workflow automation made simple',
    description: 'Create powerful automated workflows to save time and reduce errors. Send automatic reminders, follow-ups, and trigger actions based on events.',
    category: 'Productivity',
    price: 39.99,
    rating: 4.9,
    reviews: 567,
    icon: <Zap className="h-8 w-8" />,
    color: 'bg-purple-500',
    features: [
      'Visual workflow builder',
      'Email & SMS automation',
      'Custom triggers',
      'Conditional logic',
      'Integration with CRM',
      'Automation templates'
    ],
    popular: true,
    new: true
  },
  {
    id: 'analytics-dashboard',
    name: 'Analytics Dashboard',
    tagline: 'Data-driven insights for growth',
    description: 'Get deep insights into your business performance with customizable KPI dashboards and advanced reporting tools.',
    category: 'Analytics',
    price: 49.99,
    rating: 4.7,
    reviews: 189,
    icon: <BarChart3 className="h-8 w-8" />,
    color: 'bg-green-500',
    features: [
      'Custom KPI widgets',
      'Real-time data',
      'Export reports',
      'Trend analysis',
      'Mobile responsive',
      'Scheduled reports'
    ]
  },
  {
    id: 'proposals-suite',
    name: 'Proposals Suite',
    tagline: 'Win more deals with professional proposals',
    description: 'Create stunning proposals with multiple pricing options, track views, and convert more leads into customers.',
    category: 'Sales',
    price: 34.99,
    rating: 4.6,
    reviews: 412,
    icon: <FileText className="h-8 w-8" />,
    color: 'bg-orange-500',
    features: [
      'Multi-offer proposals',
      'Digital signatures',
      'View tracking',
      'Custom branding',
      'Proposal templates',
      'Conversion analytics'
    ]
  },
  {
    id: 'time-tracker',
    name: 'Time Tracker Pro',
    tagline: 'Accurate time tracking for teams',
    description: 'Track time spent on jobs, manage timeclock, and integrate with payroll. Perfect for businesses that bill by the hour.',
    category: 'Operations',
    price: 24.99,
    rating: 4.5,
    reviews: 678,
    icon: <Clock className="h-8 w-8" />,
    color: 'bg-indigo-500',
    features: [
      'One-click timers',
      'Timeclock system',
      'Payroll integration',
      'Overtime tracking',
      'Break management',
      'Time reports'
    ]
  },
  {
    id: 'customer-portal',
    name: 'Customer Portal',
    tagline: 'Self-service portal for customers',
    description: 'Give your customers access to their bookings, invoices, and history through a branded self-service portal.',
    category: 'Customer Experience',
    price: 19.99,
    rating: 4.4,
    reviews: 156,
    icon: <Users className="h-8 w-8" />,
    color: 'bg-pink-500',
    features: [
      'Booking history',
      'Invoice downloads',
      'Online payments',
      'Service requests',
      'Profile management',
      'Custom branding'
    ],
    new: true
  },
  {
    id: 'advanced-messaging',
    name: 'Advanced Messaging',
    tagline: 'Multi-channel communication hub',
    description: 'Centralize all customer communications with SMS, email, and in-app messaging. Never miss a customer message again.',
    category: 'Communication',
    price: 'free',
    rating: 4.3,
    reviews: 890,
    icon: <MessageSquare className="h-8 w-8" />,
    color: 'bg-teal-500',
    features: [
      'Two-way SMS',
      'Email integration',
      'Message templates',
      'Auto-responses',
      'Conversation history',
      'Team inbox'
    ]
  },
  {
    id: 'online-store',
    name: 'Online Store',
    tagline: 'Sell products online 24/7',
    description: 'Launch your online store and start selling products directly to customers with integrated payment processing.',
    category: 'Sales',
    price: 44.99,
    rating: 4.8,
    reviews: 234,
    icon: <ShoppingCart className="h-8 w-8" />,
    color: 'bg-red-500',
    features: [
      'Product catalog',
      'Shopping cart',
      'Payment processing',
      'Order management',
      'Shipping integration',
      'Inventory sync'
    ]
  },
  {
    id: 'smart-scheduling',
    name: 'Smart Scheduling',
    tagline: 'AI-powered scheduling optimization',
    description: 'Optimize your schedule with AI that learns patterns and suggests the best times for appointments.',
    category: 'Productivity',
    price: 54.99,
    rating: 4.9,
    reviews: 123,
    icon: <Calendar className="h-8 w-8" />,
    color: 'bg-yellow-500',
    features: [
      'AI optimization',
      'Route planning',
      'Resource balancing',
      'Conflict detection',
      'Buffer time management',
      'Predictive scheduling'
    ],
    new: true
  },
  {
    id: 'payment-hub',
    name: 'Payment Hub',
    tagline: 'All payment methods in one place',
    description: 'Accept payments via credit card, ACH, digital wallets, and more with unified reporting and reconciliation.',
    category: 'Finance',
    price: 29.99,
    rating: 4.7,
    reviews: 445,
    icon: <DollarSign className="h-8 w-8" />,
    color: 'bg-emerald-500',
    features: [
      'Multiple payment methods',
      'Recurring billing',
      'Payment links',
      'Invoice financing',
      'Fraud protection',
      'Instant payouts'
    ]
  }
];

const categories = [
  'All',
  'Operations',
  'Sales',
  'Productivity',
  'Analytics',
  'Communication',
  'Customer Experience',
  'Finance'
];

export default function AppStore() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedApp, setSelectedApp] = useState<App | null>(null);
  const [installingApp, setInstallingApp] = useState<string | null>(null);
  const [installedApps, setInstalledApps] = useState<Set<string>>(new Set(['time-tracker']));

  const filteredApps = placeholderApps.filter(app => {
    const matchesSearch = app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          app.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          app.tagline.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || app.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredApps = placeholderApps.filter(app => app.popular);
  const newApps = placeholderApps.filter(app => app.new);

  const handleInstall = async (appId: string) => {
    setInstallingApp(appId);
    // Simulate installation
    await new Promise(resolve => setTimeout(resolve, 2000));
    setInstalledApps(prev => new Set([...prev, appId]));
    setInstallingApp(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">App Store</h1>
              <span className="ml-3 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                Beta
              </span>
            </div>
            
            {/* Search Bar */}
            <div className="flex-1 max-w-lg mx-8">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search apps..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              My Apps
            </button>
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto py-4">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`whitespace-nowrap pb-2 border-b-2 font-medium text-sm transition-colors ${
                  selectedCategory === category
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Featured Section */}
        {selectedCategory === 'All' && searchQuery === '' && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Featured Apps</h2>
              <button className="text-blue-600 hover:text-blue-700 font-medium flex items-center">
                See All
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredApps.slice(0, 3).map(app => (
                <div
                  key={app.id}
                  onClick={() => setSelectedApp(app)}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-shadow cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`${app.color} text-white p-3 rounded-xl`}>
                      {app.icon}
                    </div>
                    {app.popular && (
                      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full flex items-center">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        Popular
                      </span>
                    )}
                  </div>
                  
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{app.name}</h3>
                  <p className="text-sm text-gray-500 mb-3">{app.tagline}</p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-4 w-4 ${
                              i < Math.floor(app.rating)
                                ? 'text-yellow-400 fill-current'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="ml-2 text-sm text-gray-600">
                        {app.rating} ({app.reviews})
                      </span>
                    </div>
                    
                    <div className="text-right">
                      {app.price === 'free' ? (
                        <span className="text-green-600 font-medium">Free</span>
                      ) : (
                        <span className="text-gray-900 font-medium">${app.price}/mo</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* New Apps Section */}
        {selectedCategory === 'All' && searchQuery === '' && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">New & Updated</h2>
              <button className="text-blue-600 hover:text-blue-700 font-medium flex items-center">
                See All
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
            
            <div className="flex space-x-4 overflow-x-auto pb-4">
              {newApps.map(app => (
                <div
                  key={app.id}
                  onClick={() => setSelectedApp(app)}
                  className="flex-shrink-0 w-64 bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
                >
                  <div className="flex items-center mb-3">
                    <div className={`${app.color} text-white p-2 rounded-lg`}>
                      {React.cloneElement(app.icon, { className: 'h-6 w-6' })}
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="text-sm font-semibold text-gray-900">{app.name}</h3>
                      <p className="text-xs text-gray-500">{app.category}</p>
                    </div>
                    {app.new && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                        New
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 line-clamp-2">{app.tagline}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Apps Grid */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-6">
            {selectedCategory === 'All' ? 'All Apps' : selectedCategory}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredApps.map(app => (
              <div
                key={app.id}
                onClick={() => setSelectedApp(app)}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
              >
                <div className="flex items-start mb-3">
                  <div className={`${app.color} text-white p-2 rounded-lg`}>
                    {React.cloneElement(app.icon, { className: 'h-6 w-6' })}
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-semibold text-gray-900">{app.name}</h3>
                    <p className="text-xs text-gray-500">{app.category}</p>
                  </div>
                </div>
                
                <p className="text-xs text-gray-600 mb-3 line-clamp-2">{app.tagline}</p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Star className="h-3 w-3 text-yellow-400 fill-current" />
                    <span className="ml-1 text-xs text-gray-600">{app.rating}</span>
                  </div>
                  
                  {installedApps.has(app.id) ? (
                    <span className="text-xs text-green-600 font-medium flex items-center">
                      <Check className="h-3 w-3 mr-1" />
                      Installed
                    </span>
                  ) : app.price === 'free' ? (
                    <span className="text-xs text-green-600 font-medium">Free</span>
                  ) : (
                    <span className="text-xs text-gray-900 font-medium">${app.price}/mo</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* App Detail Modal */}
      {selectedApp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center">
                <div className={`${selectedApp.color} text-white p-3 rounded-xl`}>
                  {selectedApp.icon}
                </div>
                <div className="ml-4">
                  <h2 className="text-xl font-bold text-gray-900">{selectedApp.name}</h2>
                  <p className="text-sm text-gray-500">{selectedApp.category}</p>
                </div>
              </div>
              
              <button
                onClick={() => setSelectedApp(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* App Info */}
              <div className="mb-6">
                <p className="text-gray-700 mb-4">{selectedApp.description}</p>
                
                <div className="flex items-center justify-between py-4 border-y border-gray-200">
                  <div className="flex items-center space-x-8">
                    <div>
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-5 w-5 ${
                              i < Math.floor(selectedApp.rating)
                                ? 'text-yellow-400 fill-current'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {selectedApp.rating} ({selectedApp.reviews} reviews)
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-2xl font-bold text-gray-900">
                        {selectedApp.price === 'free' ? 'Free' : `$${selectedApp.price}`}
                      </p>
                      {selectedApp.price !== 'free' && (
                        <p className="text-sm text-gray-500">per month</p>
                      )}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleInstall(selectedApp.id)}
                    disabled={installedApps.has(selectedApp.id) || installingApp === selectedApp.id}
                    className={`px-6 py-3 rounded-lg font-medium transition-all ${
                      installedApps.has(selectedApp.id)
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                        : installingApp === selectedApp.id
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {installedApps.has(selectedApp.id) ? (
                      <span className="flex items-center">
                        <Check className="h-5 w-5 mr-2" />
                        Installed
                      </span>
                    ) : installingApp === selectedApp.id ? (
                      <span className="flex items-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                        Installing...
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <Download className="h-5 w-5 mr-2" />
                        Install App
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Features */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Features</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedApp.features.map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Screenshots Placeholder */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Screenshots</h3>
                <div className="grid grid-cols-3 gap-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="bg-gray-100 rounded-lg aspect-video flex items-center justify-center">
                      <span className="text-gray-400">Screenshot {i}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
